import React, { useContext, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import { useNavigate } from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import AtomicButton from '../../atomic/button/Button';
import ListProduct from './ListProduct';
import AddEditProduct from './AddProduct';
import InputBox from '../../atomic/inputbox/InputBox';
import axios from 'axios';
import { GETPRODUCT_KITCHEN } from '../../context-api/ApiUrl';
import { ProductDataContext } from '../../context-api/data-provider/ProductDataProvider';
import { readCache, writeCache } from '../../utils/productCache';
import ProductListStatCards from './ProductListStats';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';

const toTitleCase = (str) => {
  const lowerCasePrepositions = [
    'and',
    'but',
    'for',
    'nor',
    'or',
    'so',
    'the',
    'to',
    'in',
    'on',
    'at',
    'by',
    'with',
    'a',
    'an',
    'as',
    'of',
    'up',
    'down',
    'from',
    'about',
  ];

  return str
    .split(/[\s_-]+/)
    .map((word, index, array) => {
      if (
        index === 0 ||
        index === array.length - 1 ||
        !lowerCasePrepositions.includes(word.toLowerCase())
      ) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      } else {
        return word.toLowerCase();
      }
    })
    .join(' ');
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

/** Matches Signup / SelectBox + App.css: menu list item colors and selected state */
const signupSelectMenuPaperSx = {
  '& .MuiMenuItem-root': {
    color: '#3F3F6E',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'capitalize',
    '&.Mui-selected': {
      backgroundColor: '#fdeee7 !important',
      '&:hover': {
        backgroundColor: '#fcddcf !important',
      },
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
    sx: signupSelectMenuPaperSx,
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

const getStatusMenuProps = (isCompactLayout) => ({
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: isCompactLayout ? '100%' : '200px',
    },
    sx: {
      '& .MuiMenuItem-root': {
        color: '#3F3F6E',
        fontSize: '14px',
        fontWeight: 600,
        textTransform: 'capitalize',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block',
        '&.Mui-selected': {
          backgroundColor: '#fdeee7 !important',
          '&:hover': {
            backgroundColor: '#fcddcf !important',
          },
        },
      },
    },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
});

const Product = () => {
  const navigate = useNavigate();
  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const { onDeleteProduct } = useContext(ProductDataContext);
  const [addEditProductSelected, setAddEditProductSelected] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [networkError, setNetworkError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState('None');
  const [productCategories, setProductCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [totalProducts, setTotalProducts] = useState(0);
  const [nonMealProducts, setNonMealProducts] = useState(0);
  const [mealProducts, setMealProducts] = useState(0);
  const [outOfStockProducts, setOutOfStockProducts] = useState(0);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  const KITCHEN_KEY = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.kitchenName;

  const hydrateState = (allProducts) => {
    setProducts(allProducts);
    setFilteredProducts(allProducts);
    const types = new Set(allProducts.map((p) => p.product_type));
    setProductTypes(['None', ...Array.from(types)]);
    const categories = new Set(allProducts.map((p) => p.product_category));
    setProductCategories(['None', ...Array.from(categories)]);
    setTotalProducts(allProducts.length);
    setNonMealProducts(allProducts.filter((p) => p.product_type === 'non_meal_item').length);
    setMealProducts(allProducts.filter((p) => p.product_type === 'meal_item').length);
    setOutOfStockProducts(allProducts.filter((p) => p.available_quantity <= 5).length);
  };

  useEffect(() => {
    if (!KITCHEN_KEY) return;
    setAddEditProductSelected(false);

    const cached = readCache(KITCHEN_KEY);
    if (cached) {
      hydrateState(cached.data);
      setLastRefreshAt(cached.cachedAt || null);
    } else {
      setIsLoading(true);
      fetchAndRefresh();
    }

    const handleResize = () => {
      adjustItemsPerPage();
      setViewportWidth(window.innerWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [KITCHEN_KEY]);

  const adjustItemsPerPage = () => {};

  const fetchAndRefresh = async () => {
    if (!KITCHEN_KEY) return;
    try {
      const response = await axios.post(GETPRODUCT_KITCHEN, {
        kitchen_names: [KITCHEN_KEY],
      });
      const allProducts = flattenObject(response.data.data[KITCHEN_KEY]);
      const now = Date.now();
      writeCache(KITCHEN_KEY, allProducts);
      hydrateState(allProducts);
      setLastRefreshAt(now);
    } catch (error) {
      console.log('Error fetching products:', error);
      setNetworkError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getProducts = () => {
    setIsLoading(true);
    fetchAndRefresh();
  };

  function flattenObject(obj) {
    let result = [];
    for (let key in obj) {
      if (Array.isArray(obj[key])) {
        result = result.concat(obj[key]);
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        result = result.concat(flattenObject(obj[key]));
      }
    }
    return result;
  }

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    let filtered = products;

    if (query) {
      const lowered = query.toLowerCase();
      filtered = filtered.filter((product) => {
        const nameMatch = (product.product_name || '').toLowerCase().includes(lowered);
        const priceString = String(product.selling_price ?? product.price ?? '');
        const priceMatch = priceString.toLowerCase().includes(lowered);
        return nameMatch || priceMatch;
      });
    }

    if (selectedProductType !== 'None') {
      filtered = filtered.filter((product) => product.product_type === selectedProductType);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter((product) => product.status_type === selectedStatus);
    }

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) =>
        selectedCategories.includes(product.product_category)
      );
    }

    setFilteredProducts(filtered);
  };

  const handleProductTypeChange = (selectedType) => {
    setSelectedProductType(selectedType);

    let filtered = products;

    if (selectedType === 'None') {
      filtered = products;
    } else {
      filtered = filtered.filter((product) => product.product_type === selectedType);
    }

    setSelectedCategories([]);
    const categoriesForType = new Set(filtered.map((product) => product.product_category));
    setProductCategories(['None', ...Array.from(categoriesForType)]);

    if (searchQuery) {
      const lowered = searchQuery.toLowerCase();
      filtered = filtered.filter((product) => {
        const nameMatch = (product.product_name || '').toLowerCase().includes(lowered);
        const priceString = String(product.selling_price ?? product.price ?? '');
        const priceMatch = priceString.toLowerCase().includes(lowered);
        return nameMatch || priceMatch;
      });
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter((product) => product.status_type === selectedStatus);
    }

    setFilteredProducts(filtered);
  };

  const handleCategoryChange = (event) => {
    const {
      target: { value: rawValue },
    } = event;

    let value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

    // Treat "None" as a virtual option: selecting it clears other selections.
    if (value.includes('None')) {
      value = [];
    }

    setSelectedCategories(value);

    let filtered = products;

    if (value.length > 0) {
      filtered = filtered.filter((product) => value.includes(product.product_category));
    }

    if (searchQuery) {
      const lowered = searchQuery.toLowerCase();
      filtered = filtered.filter((product) => {
        const nameMatch = (product.product_name || '').toLowerCase().includes(lowered);
        const priceString = String(product.selling_price ?? product.price ?? '');
        const priceMatch = priceString.toLowerCase().includes(lowered);
        return nameMatch || priceMatch;
      });
    }

    if (selectedProductType !== 'None') {
      filtered = filtered.filter((product) => product.product_type === selectedProductType);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter((product) => product.status_type === selectedStatus);
    }

    setFilteredProducts(filtered);
  };

  const handleCategoryOpen = () => {
    setCategoryOpen(true);
  };

  const handleCategoryClose = () => {
    setCategoryOpen(false);
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    setSelectedStatus(value);

    let filtered = products;

    if (searchQuery) {
      filtered = filtered.filter((product) =>
        product.product_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedProductType !== 'None') {
      filtered = filtered.filter((product) => product.product_type === selectedProductType);
    }

    if (selectedCategories.length > 0) {
      filtered = filtered.filter((product) =>
        selectedCategories.includes(product.product_category)
      );
    }

    if (value !== 'all') {
      filtered = filtered.filter((product) => product.status_type === value);
    }

    setFilteredProducts(filtered);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const value = Number(e.target.value);
    if (!Number.isNaN(value) && value > 0) {
      setItemsPerPage(value);
      setCurrentPage(1);
    }
  };

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const isCompactLayout = viewportWidth <= 700;
  const formatProductTypeLabel = (type) => {
    if (type === 'non_meal_item') return 'Regular';
    if (type === 'meal_item') return 'Combo';
    if (type === 'None') return 'None';
    return toTitleCase((type || '').replace(/_/g, ' '));
  };
  const formatLastRefresh = (timestamp) => {
    if (!timestamp) return 'Never';
    try {
      return new Date(timestamp).toLocaleString([], {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Never';
    }
  };

  /** Signup / InputBox + SelectBox: border default #D7E0F2, focus #3F3F6E, label + select text */
  const sharedInputSx = {
    '& .MuiInputLabel-root': {
      color: '#3F3F6E',
      fontSize: '13px',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#3F3F6E',
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      color: '#3F3F6E',
      fontWeight: 500,
      paddingLeft: '4px',
    },
    '& .MuiOutlinedInput-root': {
      height: '42px',
      borderRadius: '5px',
      backgroundColor: '#fff',
      transition: 'all 0.2s ease',
      '& fieldset': {
        borderWidth: '1px',
        borderColor: '#D7E0F2',
      },
      '&:hover fieldset': {
        borderColor: '#3F3F6E',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3F3F6E',
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#3F3F6E !important',
      },
      '& input': {
        fontSize: '13px',
      },
      '& .MuiSelect-select': {
        fontSize: '13px',
        color: '#3F3F6E',
        textTransform: 'capitalize',
      },
      '& .MuiSelect-icon': {
        color: '#3F3F6E',
      },
    },
  };

  const controlBarStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '16px',
    background: '#f2f2f2ff',
    border: '1px solid #b5b5b5ff',
    borderRadius: '5px',
    boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)',
    padding: '14px 16px',
  };

  const topRowStyle = {
    display: 'flex',
    alignItems: isCompactLayout ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: isCompactLayout ? 'wrap' : 'nowrap',
    width: isCompactLayout ? '100%' : 'auto',
  };

  const filterGroupStyle = {
    display: 'flex',
    alignItems: isCompactLayout ? 'stretch' : 'center',
    gap: '12px',
    flexWrap: isCompactLayout ? 'wrap' : 'nowrap',
    flex: '1 1 auto',
    minWidth: 0,
  };

  const bottomRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: isCompactLayout ? 'wrap' : 'nowrap',
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'approved', label: 'Approved' },
    { value: 'merchant_image_review_pending', label: 'Image review pending (Merchant)' },
    { value: 'merchant_content_review_pending', label: 'Content review pending (Merchant)' },
    { value: 'content_rejected_by_admin', label: 'Content rejected by admin' },
    { value: 'image_rejected_by_admin', label: 'Image rejected by admin' },
    { value: 'admin_review_pending', label: 'Admin review pending' },
    { value: 'content_rejected_by_merchant', label: 'Content rejected by merchant' },
    { value: 'image_rejected_by_merchant', label: 'Image rejected by merchant' },
    { value: 'content_approved_merchant', label: 'Content approved by merchant' },
    { value: 'merchant_review_pending', label: 'Merchant review pending' },
    { value: 'ai_generated_merchant_review_pending', label: 'AI generated merchant review pending' },
  ];

  return (
    <div
      style={{
        // width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#f5f5f5',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          padding: isCompactLayout ? '14px' : '24px',
          maxWidth: '950px',
        }}
      >
        <div style={bottomRowStyle}>
          <div style={{ fontFamily: 'Inter Medium', fontSize: '28px' }}> Menu List</div>

          <AtomicButton
            label='ADD PRODUCT'
            handleSubmit={() => {
              navigate('/addproduct');
              setAddEditProductSelected(!addEditProductSelected);
            }}
            style={{
              borderRadius: '5px',
              width: isCompactLayout ? '100%' : '170px',
              height: '42px',
              fontWeight: '600',
              letterSpacing: '0.3px',
            }}
          />
        </div>

        <div style={controlBarStyle}>
          <div style={topRowStyle}>
            <InputBox
              type='text'
              label='Search by product name or price'
              size='small'
              height='auto'
              sx={{
                width: isCompactLayout ? '100%' : '260px',
                ...sharedInputSx,
              }}
              name='search'
              value={searchQuery}
              onChange={handleSearchChange}
            />

            <div style={filterGroupStyle}>
              <FormControl
                size='small'
                sx={{
                  minWidth: isCompactLayout ? '100%' : '160px',
                  ...sharedInputSx,
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <InputLabel id='menu-type-select-label'>Menu Type</InputLabel>
                <Select
                  labelId='menu-type-select-label'
                  id='menu-type-select'
                  value={selectedProductType}
                  onChange={(e) => handleProductTypeChange(e.target.value)}
                  input={<OutlinedInput label='Menu Type' />}
                  MenuProps={MenuProps}
                >
                  {productTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {formatProductTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{
                  minWidth: isCompactLayout ? '100%' : '220px',
                  ...sharedInputSx,
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                <InputLabel id='category-select-label'>Product Category</InputLabel>
                <Select
                  labelId='category-select-label'
                  id='category-select'
                  multiple
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label='Product Category' />}
                  renderValue={(selected) => {
                    if (!selected.length) {
                      return 'None';
                    }
                    const maxChips = 2;
                    const visible = selected.slice(0, maxChips);
                    const rest = selected.length - maxChips;
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexWrap: 'nowrap',
                          overflow: 'hidden',
                          minHeight: 24,
                        }}
                      >
                        {visible.map((value) => (
                          <Chip
                            key={value}
                            label={toTitleCase(value)}
                            size='small'
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                        {rest > 0 && (
                          <span style={{ fontSize: '12px', color: '#f97316', flexShrink: 0 }}>
                            +{rest} more
                          </span>
                        )}
                      </Box>
                    );
                  }}
                  MenuProps={MenuProps}
                  open={categoryOpen}
                  onOpen={handleCategoryOpen}
                  onClose={handleCategoryClose}
                >
                  {productCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {toTitleCase(category)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                size='small'
                sx={{
                  width: isCompactLayout ? '100%' : '200px',
                  minWidth: isCompactLayout ? '100%' : '200px',
                  maxWidth: isCompactLayout ? '100%' : '200px',
                  ...sharedInputSx,
                  '& .MuiSelect-select': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  },
                }}
              >
                <InputLabel id='status-select-label'>Status</InputLabel>
                <Select
                  labelId='status-select-label'
                  id='status-select'
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  label='Status'
                  MenuProps={getStatusMenuProps(isCompactLayout)}
                >
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div style={bottomRowStyle}>
            <div
              style={{
                display: 'flex',
                flexDirection: isCompactLayout ? 'row' : 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '6px',
                fontSize: '12px',
                color: '#6f6f6fff',
              }}
            >
              <span>
                Last updated{' '}
                <span style={{ fontWeight: 600 }}>{formatLastRefresh(lastRefreshAt)}</span>
              </span>
              <span>·</span>
              <button
                type='button'
                onClick={getProducts}
                disabled={isLoading}
                style={{
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  color: '#000000ff',
                  textDecoration: 'underline',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        <ProductListStatCards products={products} />

        <ListProduct
          getProducts={getProducts}
          filteredProducts={filteredProducts}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          toTitleCase={toTitleCase}
          formatProductTypeLabel={formatProductTypeLabel}
          onDeleteProduct={onDeleteProduct}
          onRefresh={getProducts}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '20px',
          }}
        >
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              padding: '6px 14px',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              background: 'white',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              color: currentPage === 1 ? '#ccc' : '#333',
            }}
          >
            Previous
          </button>
          <span style={{ fontSize: '13px', color: '#666' }}>
            Page {currentPage} of {totalPages} &nbsp;·&nbsp; {filteredProducts.length} items
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{
              padding: '6px 14px',
              border: '1px solid #d0d0d0',
              borderRadius: '4px',
              background: 'white',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              color: currentPage === totalPages ? '#ccc' : '#333',
            }}
          >
            Next
          </button>
          <span style={{ fontSize: '13px', color: '#999', marginLeft: '8px' }}>|</span>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: '#666',
            }}
          >
            Show
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{
                padding: '6px 10px',
                border: '1px solid #d0d0d0',
                borderRadius: '4px',
                background: 'white',
                fontSize: '13px',
                color: '#333',
                cursor: 'pointer',
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            per page
          </label>
        </div>
      </div>
    </div>
  );
};

export default Product;
